import { computed, inject, Injectable, Injector, Signal, signal } from '@angular/core';
import {
  explore,
  ForbiddenError,
  ReadonlyRepositoryCollectionService,
  ServerError,
  withSearch,
  withSort,
} from '@softline/core';
import { handleRequestError } from '@softline/application';
import { ShelfLabelRepository } from '../repositories/shelf-label.repository';
import { ShelfLabelUpdateRepository } from '../repositories/shelf-label-update.repository';
import { ShelfLabelQueryStateService } from './shelf-label-query-state.service';
import { LabelType, QueryShelfLabelRequest, ShelfLabelDefinition } from '../types';

export interface LabelTypeCount {
  labelType: LabelType | 'ALL';
  count: number;
}

export type ArticleLabelViewState =
  'loading' | 'empty' | 'forbidden' | 'service-error' | 'populated';

const LABEL_TYPES: LabelType[] = ['regaletikett', 'fahnchen', 'artikeletikett'];

// Placeholder request used only while storeId/priceLevelId defaults are still
// resolving. The constructor passes `skipInitialLoad: true` to the base
// class, which makes the resource's very first automatic invocation a no-op
// (see `ReadonlyRepositoryCollectionService` / `cancelableResource`), so this
// value is never actually sent to the backend.
const PENDING_REQUEST: QueryShelfLabelRequest = {
  storeId: 1,
  priceLevelId: 1,
  promotionFilter: 'ALL',
  onlyProductsWithStockQuantity: false,
  onlyProductsWithStorageLocation: false,
};

@Injectable()
export class ShelfLabelService extends ReadonlyRepositoryCollectionService<
  ShelfLabelDefinition,
  ShelfLabelDefinition[],
  QueryShelfLabelRequest
> {
  private readonly updateRepository = inject(ShelfLabelUpdateRepository);
  private readonly queryState = inject(ShelfLabelQueryStateService);
  private readonly injector = inject(Injector);

  override readonly params: Signal<QueryShelfLabelRequest> = computed(
    () => this.queryState.confirmedRequest() ?? PENDING_REQUEST
  );

  readonly explorer = explore(this.all, withSearch(), withSort());

  readonly activeLabelTypeTab = signal<LabelType | 'ALL'>('ALL');

  readonly labelTypeCounts: Signal<LabelTypeCount[]> = computed(() => {
    const all = this.all();
    return [
      { labelType: 'ALL' as const, count: all.length },
      ...LABEL_TYPES.map((labelType) => ({
        labelType,
        count: all.filter((row) => row.labelType.key === labelType).length,
      })),
    ];
  });

  readonly filteredRows: Signal<ShelfLabelDefinition[]> = computed(() => {
    const tab = this.activeLabelTypeTab();
    const rows = this.explorer.result() ?? [];
    return tab === 'ALL' ? rows : rows.filter((row) => row.labelType.key === tab);
  });

  readonly selectedRows: Signal<ShelfLabelDefinition[]> = computed(() =>
    this.all().filter((row) => row.selected)
  );

  readonly viewState: Signal<ArticleLabelViewState> = computed(() => {
    if (this.filteredRows().length > 0) return 'populated';
    if (this.isLoading()) return 'loading';
    const err = this.error();
    if (err instanceof ForbiddenError) return 'forbidden';
    if (err instanceof ServerError) return 'service-error';
    return 'empty';
  });

  constructor() {
    super(inject(ShelfLabelRepository), { skipInitialLoad: true });
  }

  protected override getAll(
    result: ShelfLabelDefinition[] | null | undefined
  ): ShelfLabelDefinition[] {
    return result ?? [];
  }

  async toggleSelected(id: string, selected: boolean): Promise<boolean> {
    try {
      const updated = await this.updateRepository.patch(id, { selected });
      this.all.set(this.all().map((row) => (row.id === id ? updated : row)));
      return true;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return false;
    }
  }

  async updateLabelType(ids: string[], labelType: LabelType): Promise<boolean> {
    const previous = new Map(
      this.all()
        .filter((row) => ids.includes(row.id))
        .map((row) => [row.id, row.labelType] as const)
    );
    this.all.set(
      this.all().map((row) =>
        ids.includes(row.id) ? { ...row, labelType: { key: labelType } } : row
      )
    );
    try {
      await Promise.all(
        ids.map((id) => this.updateRepository.patch(id, { labelType: { key: labelType } }))
      );
      return true;
    } catch (e) {
      this.all.set(
        this.all().map((row) =>
          previous.has(row.id) ? { ...row, labelType: previous.get(row.id)! } : row
        )
      );
      handleRequestError(e, this.injector, { show: true });
      return false;
    }
  }
}
